import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EditOutlined,
  FilePdfOutlined,
  LaptopOutlined,
  RollbackOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Switch,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { errorText } from '@/components/email-templates/useEmployeeNoticesT';
import { HowToCard } from '@/components/how-to-card';
import {
  cancelZaz,
  downloadZazPdf,
  resendZaz,
  returnZaz,
  saveZaz,
} from '@/services/zaz.service';
import { DATE_FORMATS } from '@/utils/formUtils';
import { EMPLOYEE_ZAZ_QUERY_KEY } from './zazKeys';

const GUIDE_ICONS = [
  SendOutlined,
  EditOutlined,
  RollbackOutlined,
  FilePdfOutlined,
];
const ROUND = '!rounded-[50px]';

const fmtDate = (value) =>
  value ? dayjs(value).format(DATE_FORMATS.DISPLAY) : '';

const allFields = (sections) => sections.flatMap((section) => section.fields);

const isEmpty = (value) =>
  value === undefined || value === null || value === '';

// A select answer saved before the field had options (free text).
const legacySelectValue = (field, value) =>
  field.type === 'select' && !isEmpty(value) && !field.options.includes(value)
    ? String(value)
    : '';

// Stored answers → form values (dates as dayjs; an old free-text select
// answer as empty, so the head picks an option).
const toFormValues = (answers = {}, sections = []) =>
  Object.fromEntries(
    allFields(sections).map((field) => {
      const value = answers[field.key];
      if (field.type === 'boolean') return [field.key, value === true];
      if (field.type === 'date') {
        return [field.key, value ? dayjs(value, 'YYYY-MM-DD') : null];
      }
      if (legacySelectValue(field, value)) return [field.key, undefined];
      return [field.key, value === '' ? undefined : value];
    })
  );

// Form values → what the server takes (dates as YYYY-MM-DD, empty as '').
// An old free-text select answer the head left alone is not sent, so the
// server keeps it as stored.
const fromFormValues = (values = {}, sections = [], answers = {}) =>
  Object.fromEntries(
    allFields(sections)
      .filter(
        (field) =>
          !(
            legacySelectValue(field, answers[field.key]) &&
            isEmpty(values[field.key])
          )
      )
      .map((field) => {
        const value = values[field.key];
        if (field.type === 'boolean') return [field.key, value === true];
        if (field.type === 'date') {
          return [field.key, value ? dayjs(value).format('YYYY-MM-DD') : ''];
        }
        return [field.key, value ?? ''];
      })
  );

// `rest` carries what Form.Item injects (value, onChange, id…): without it
// the control is not bound to the form.
function FieldInput({ field, t, ...rest }) {
  switch (field.type) {
    case 'boolean':
      return <Switch {...rest} />;
    case 'textarea':
      return (
        <Input.TextArea
          {...rest}
          maxLength={field.max}
          autoSize={{ minRows: 2, maxRows: 8 }}
        />
      );
    case 'number':
      return (
        <InputNumber
          {...rest}
          min={field.min}
          max={field.max}
          precision={0}
          className="!w-full"
        />
      );
    case 'date':
      return (
        <DatePicker
          {...rest}
          format={DATE_FORMATS.DISPLAY}
          className="!w-full"
        />
      );
    case 'select':
      return (
        <Select
          {...rest}
          allowClear={!field.required}
          options={field.options.map((option) => ({
            value: option,
            label: t(`options.${field.key}.${option}`),
          }))}
        />
      );
    default:
      return <Input {...rest} maxLength={field.max} />;
  }
}

const valueText = (field, value, t) => {
  if (field.type === 'boolean') return t(value === true ? 'yes' : 'no');
  if (isEmpty(value)) return '—';
  if (field.type === 'date') return fmtDate(value);
  if (field.type === 'select') {
    return (
      legacySelectValue(field, value) || t(`options.${field.key}.${value}`)
    );
  }
  return String(value);
};

// Form.Item rules: a required switch must be on; other required fields filled.
const fieldRules = (field, t) => {
  if (!field.required) return [];
  if (field.type === 'boolean') {
    return [
      {
        validator: (_, value) =>
          value === true
            ? Promise.resolve()
            : Promise.reject(new Error(t('directorRequired'))),
      },
    ];
  }
  return [{ required: true, message: t('requiredMark') }];
};

/** The head's form for the open ZAZ: save a draft or return it to HR. */
function ZazHeadForm({ employeeId, item, sections, t, onDone }) {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue(toFormValues(item.answers, sections));
  }, [form, item, sections]);

  const draft = useMutation({
    mutationFn: () =>
      saveZaz(
        employeeId,
        item._id,
        fromFormValues(form.getFieldsValue(true), sections, item.answers)
      ),
    onSuccess: (result) => {
      message.success(result?.message);
      onDone();
    },
    onError: (error) => message.error(errorText(error, t('actionFailed'))),
  });
  const giveBack = useMutation({
    mutationFn: (values) =>
      returnZaz(
        employeeId,
        item._id,
        fromFormValues(values, sections, item.answers)
      ),
    onSuccess: (result) => {
      if (result?.notified === false) message.warning(result?.message);
      else message.success(result?.message);
      onDone();
    },
    onError: (error) => message.error(errorText(error, t('actionFailed'))),
  });

  const submitReturn = async () => {
    try {
      const values = await form.validateFields();
      giveBack.mutate({ ...form.getFieldsValue(true), ...values });
    } catch {
      // Antd marks the missing fields.
    }
  };

  return (
    <Form form={form} layout="vertical" autoComplete="off" className="mt-2">
      {sections.map((section) => (
        <section key={section.key} className="mb-3">
          <h4 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-500">
            {t(`sections.${section.key}`)}
          </h4>
          <div className="grid gap-x-3 sm:grid-cols-2">
            {section.fields.map((field) => {
              const legacy = legacySelectValue(
                field,
                item.answers?.[field.key]
              );
              return (
                <Form.Item
                  key={field.key}
                  name={field.key}
                  label={t(`fields.${field.key}`)}
                  className={
                    field.type === 'textarea' || field.type === 'boolean'
                      ? '!mb-2 sm:col-span-2'
                      : '!mb-2'
                  }
                  required={field.required}
                  rules={fieldRules(field, t)}
                  valuePropName={field.type === 'boolean' ? 'checked' : 'value'}
                  extra={
                    legacy ? t('legacyValue', { value: legacy }) : undefined
                  }
                >
                  <FieldInput field={field} t={t} />
                </Form.Item>
              );
            })}
          </div>
        </section>
      ))}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          className={ROUND}
          loading={draft.isPending}
          onClick={() => draft.mutate()}
        >
          {t('saveDraft')}
        </Button>
        <Popconfirm title={t('returnConfirm')} onConfirm={submitReturn}>
          <Button type="primary" className={ROUND} loading={giveBack.isPending}>
            {t('returnToHr')}
          </Button>
        </Popconfirm>
      </div>
    </Form>
  );
}

function ZazAnswers({ item, sections, t }) {
  return (
    <div className="mt-2 grid gap-3">
      {sections.map((section) => (
        <section key={section.key}>
          <h4 className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-500">
            {t(`sections.${section.key}`)}
          </h4>
          <dl className="m-0 grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-1 text-[12.5px]">
            {section.fields.map((field) => (
              <div key={field.key} className="contents">
                <dt className="text-zinc-500">{t(`fields.${field.key}`)}</dt>
                <dd className="m-0 whitespace-pre-line text-zinc-900">
                  {valueText(field, item.answers?.[field.key], t)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}

/**
 * The profile's ZAZ tile. HR sends it to the heads of the sector, sends it
 * again or back for completion, cancels it and downloads the PDF; a head
 * fills in the open one and returns it to HR. The employee never gets here
 * (the server refuses them). Equipment stays with the existing equipment
 * request: a shortcut to it, nothing more.
 *
 * @param {Object} props
 * @param {string} props.employeeId
 * @param {Object} props.zaz - useEmployeeZaz().zaz
 * @param {Function} [props.onSend] - HR: open the send dialog.
 * @param {Function} [props.onRequestEquipment] - The existing "Zatraži opremu".
 */
export default function ZazPanel({
  employeeId,
  zaz,
  onSend,
  onRequestEquipment,
}) {
  const { t } = useTranslation(['zaz', 'forms']);
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: [EMPLOYEE_ZAZ_QUERY_KEY, employeeId],
    });

  const resend = useMutation({
    mutationFn: (zazId) => resendZaz(employeeId, zazId),
    onSuccess: (result) => {
      if (result?.notified === false) message.warning(result?.message);
      else message.success(result?.message);
      refresh();
    },
    onError: (error) => message.error(errorText(error, t('actionFailed'))),
  });
  const cancel = useMutation({
    mutationFn: (zazId) => cancelZaz(employeeId, zazId),
    onSuccess: (result) => {
      message.success(result?.message);
      refresh();
    },
    onError: (error) => message.error(errorText(error, t('actionFailed'))),
  });
  const pdf = useMutation({
    mutationFn: (zazId) => downloadZazPdf(employeeId, zazId),
    onError: () => message.error(t('pdfFailed')),
  });

  if (!zaz) return null;
  const sections = zaz.sections || [];
  const items = zaz.items || [];
  const guideSteps = t('guide.steps', { returnObjects: true });

  return (
    <div className="mt-3 grid gap-3">
      <HowToCard
        title={t('guide.title')}
        open={items.length === 0}
        sections={[
          {
            title: t('guide.sectionTitle'),
            steps: (Array.isArray(guideSteps) ? guideSteps : []).map(
              (step, i) => ({ ...step, icon: GUIDE_ICONS[i] })
            ),
          },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        {zaz.canSend && onSend && (
          <Button
            type="primary"
            className={ROUND}
            icon={<SendOutlined />}
            onClick={onSend}
          >
            {t('sendAction')}
          </Button>
        )}
        {onRequestEquipment && (
          <Button
            className={ROUND}
            icon={<LaptopOutlined />}
            onClick={onRequestEquipment}
          >
            {t('requestEquipment')}
          </Button>
        )}
      </div>
      {onRequestEquipment && (
        <p className="m-0 text-[12px] text-zinc-500">{t('equipmentHint')}</p>
      )}

      {items.length === 0 && (
        <p className="m-0 text-[13px] text-zinc-500">{t('empty')}</p>
      )}

      {items.map((item) => {
        const isOpen = item.status === 'sent';
        const canFill = zaz.isHead && isOpen;
        return (
          <section
            key={item._id}
            className="grid gap-1 rounded-xl border border-zinc-200 bg-white p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="m-0 text-[13px] font-bold text-zinc-900">
                {t('title')}
              </h3>
              <Tag
                color={isOpen ? 'gold' : 'green'}
                className="!rounded-[50px]"
              >
                {t(`status.${item.status}`)}
              </Tag>
            </div>
            <div className="grid gap-0.5 text-[12px] text-zinc-600">
              <span>
                {t('sentInfo', {
                  name: item.sentByName,
                  date: fmtDate(item.sentAt),
                })}
              </span>
              {item.heads?.length > 0 && (
                <span>
                  {t('headsInfo', {
                    names: item.heads.map((head) => head.name).join(', '),
                  })}
                </span>
              )}
              {isOpen && item.savedAt && (
                <span>
                  {t('savedInfo', {
                    name: item.savedByName,
                    date: fmtDate(item.savedAt),
                  })}
                </span>
              )}
              {item.returnedAt && !isOpen && (
                <span>
                  {t('returnedInfo', {
                    name: item.returnedByName,
                    date: fmtDate(item.returnedAt),
                  })}
                </span>
              )}
            </div>
            {item.note && (
              <p className="m-0 whitespace-pre-line rounded-lg bg-zinc-50 p-2 text-[12.5px] text-zinc-700">
                <span className="font-semibold">{t('noteFromHr')}:</span>{' '}
                {item.note}
              </p>
            )}

            {canFill ? (
              <ZazHeadForm
                employeeId={employeeId}
                item={item}
                sections={sections}
                t={t}
                onDone={refresh}
              />
            ) : (
              <>
                {isOpen && (
                  <p className="m-0 mt-1 text-[12.5px] text-zinc-500">
                    {t('waitingForHead')}
                  </p>
                )}
                <ZazAnswers item={item} sections={sections} t={t} />
              </>
            )}

            <div className="mt-2 flex flex-wrap justify-end gap-2">
              <Button
                size="small"
                className={ROUND}
                icon={<FilePdfOutlined />}
                loading={pdf.isPending && pdf.variables === item._id}
                onClick={() => pdf.mutate(item._id)}
              >
                {t('pdf')}
              </Button>
              {zaz.isHr && (
                <Popconfirm
                  title={t(isOpen ? 'resendConfirm' : 'reopenConfirm')}
                  onConfirm={() => resend.mutate(item._id)}
                >
                  <Button
                    size="small"
                    className={ROUND}
                    loading={resend.isPending && resend.variables === item._id}
                  >
                    {t(isOpen ? 'resend' : 'reopen')}
                  </Button>
                </Popconfirm>
              )}
              {zaz.isHr && (
                <Popconfirm
                  title={t('cancelConfirm')}
                  onConfirm={() => cancel.mutate(item._id)}
                >
                  <Button
                    size="small"
                    danger
                    className={ROUND}
                    loading={cancel.isPending && cancel.variables === item._id}
                  >
                    {t('cancel')}
                  </Button>
                </Popconfirm>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
